<template>
  <div class="platform-dashboard" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">Platform Overview</h1>
      <p class="page-subtitle">Monitor and manage all tenants from a single dashboard</p>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card gradient-purple">
        <div class="stat-icon">
          <i class="pi pi-building"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.totalTenants }}</span>
          <span class="stat-label">Total Tenants</span>
        </div>
      </div>

      <div class="stat-card gradient-green">
        <div class="stat-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.activeTenants }}</span>
          <span class="stat-label">Active Tenants</span>
        </div>
      </div>

      <div class="stat-card gradient-yellow">
        <div class="stat-icon">
          <i class="pi pi-clock"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.provisioningTenants }}</span>
          <span class="stat-label">Provisioning</span>
        </div>
      </div>

      <div class="stat-card gradient-red">
        <div class="stat-icon">
          <i class="pi pi-ban"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.suspendedTenants }}</span>
          <span class="stat-label">Suspended</span>
        </div>
      </div>
    </div>

    <!-- Plans Distribution -->
    <div class="dashboard-section">
      <h2 class="section-title">
        <i class="pi pi-chart-bar"></i>
        Tenants by Plan
      </h2>
      <div class="plans-grid">
        <div
          v-for="(count, plan) in stats.byPlan"
          :key="plan"
          class="plan-card"
          :class="`plan-${plan}`"
        >
          <span class="plan-name">{{ formatPlanName(plan) }}</span>
          <span class="plan-count">{{ count }}</span>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="dashboard-section">
      <h2 class="section-title">
        <i class="pi pi-bolt"></i>
        Quick Actions
      </h2>
      <div class="actions-grid">
        <button @click="goToCreateTenant" class="action-card">
          <div class="action-icon gradient-purple">
            <i class="pi pi-plus"></i>
          </div>
          <span class="action-label">Create Tenant</span>
        </button>

        <button @click="goToTenants" class="action-card">
          <div class="action-icon gradient-blue">
            <i class="pi pi-list"></i>
          </div>
          <span class="action-label">View All Tenants</span>
        </button>

        <button @click="goToAudit" class="action-card">
          <div class="action-icon gradient-yellow">
            <i class="pi pi-history"></i>
          </div>
          <span class="action-label">Audit Logs</span>
        </button>

        <button @click="refreshStats" class="action-card">
          <div class="action-icon gradient-green">
            <i class="pi pi-refresh" :class="{ 'spinning': loading }"></i>
          </div>
          <span class="action-label">Refresh Stats</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';

export default {
  name: 'PlatformDashboard',
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      stats: {
        totalTenants: 0,
        activeTenants: 0,
        suspendedTenants: 0,
        provisioningTenants: 0,
        byPlan: {
          free: 0,
          standard: 0,
          premium: 0,
          enterprise: 0,
        },
      },
    };
  },
  mounted() {
    this.loadStats();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const stats = await platformApi.getStats();
        if (stats) {
          this.stats = stats;
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        this.loading = false;
      }
    },
    refreshStats() {
      this.loadStats();
    },
    formatPlanName(plan) {
      return plan.charAt(0).toUpperCase() + plan.slice(1);
    },
    goToCreateTenant() {
      this.$router.push('/platform/tenants/new');
    },
    goToTenants() {
      this.$router.push('/platform/tenants');
    },
    goToAudit() {
      this.$router.push('/platform/audit');
    },
  },
};
</script>

<style scoped>
/* Light Theme (Default) */
.platform-dashboard {
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --bg-card: #ffffff;
  --bg-card-alt: #f8f8f8;
  --border-color: #e7e7e7;
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  max-width: 1400px;
  margin: 0 auto;
  font-family: 'AsenPro', sans-serif;
  transition: all 0.3s ease;
}

/* Dark Theme */
.platform-dashboard.dark-theme {
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --bg-card: #2a2a24;
  --bg-card-alt: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--text-secondary);
  margin-top: 0.5rem;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  padding: 1.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--bg-card);
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow);
}

.gradient-purple {
  background: linear-gradient(135deg, rgba(228, 130, 26, 0.3), rgba(228, 130, 26, 0.1));
}

.gradient-green {
  background: linear-gradient(135deg, rgba(174, 234, 183, 0.3), rgba(174, 234, 183, 0.1));
}

.gradient-yellow {
  background: linear-gradient(135deg, rgba(250, 212, 158, 0.3), rgba(250, 212, 158, 0.1));
}

.gradient-red {
  background: linear-gradient(135deg, rgba(228, 75, 85, 0.3), rgba(228, 75, 85, 0.1));
}

.gradient-blue {
  background: linear-gradient(135deg, rgba(200, 58, 119, 0.3), rgba(200, 58, 119, 0.1));
}

.stat-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.15);
  border-radius: 0.75rem;
  font-size: 1.5rem;
  color: #e4821a;
  position: relative;
  z-index: 1;
}

.stat-content {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* Sections */
.dashboard-section {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow);
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-title i {
  color: #e4821a;
}

/* Plans Grid */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.plan-card {
  padding: 1.25rem;
  border-radius: 0.75rem;
  text-align: center;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  transition: all 0.2s;
}

.plan-card:hover {
  transform: translateY(-2px);
  border-color: rgba(228, 130, 26, 0.4);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.plan-name {
  display: block;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.plan-count {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
}

.plan-free { border-color: rgba(168, 168, 160, 0.3); }
.plan-standard { border-color: rgba(250, 212, 158, 0.3); }
.plan-premium { border-color: rgba(228, 130, 26, 0.3); }
.plan-enterprise { border-color: rgba(200, 58, 119, 0.3); }

/* Actions Grid */
.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.action-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.action-card:hover {
  background: rgba(228, 130, 26, 0.08);
  border-color: rgba(228, 130, 26, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.action-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  font-size: 1.25rem;
  color: white;
}

.action-label {
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 500;
}

/* Spinning animation */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}
</style>
